import { useMemo } from "react";
import { sortBy } from "lodash-es";
import axios, { AxiosResponse } from "axios";
import * as Yup from "yup";

import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useParams, useOutletContext, useLocation } from "react-router-dom";

import {
  About,
  LayerPropertyValue,
  MapPosition,
  MapPositionResponse,
  Settings,
} from "types";
import { LayerPropertyValueResponse, SettingsResponse } from "types/backend";

import { UnauthorizedError } from "errors/UnauthorizedError";
import { deserializeIndicator } from "utils/IndicatorUtils";

export const useConfig = () => {
  const { config } = useParams();

  if (!config) throw Error("No configuration provided");

  return config;
};

function deserializeSettings(response: SettingsResponse): Settings {
  return {
    ...response,
    backgroundIndicators: (response.backgroundIndicators || []).map((layer) =>
      deserializeIndicator(layer, [])
    ),
    masks: (response.masks || [])
      .map((mask) => deserializeIndicator(mask, []))
      .filter((maskIndicator) => maskIndicator.layers.length > 0),
    initialPosition: deserializeMapPosition(response.initialPosition),
    layerProperties: sortBy(
      (response.variables || []).map((property, i) => ({
        ...property,
        id: property.id || i,
        direction: property.direction || "row",
        type: property.type || "radio",
        values: sortBy(property.values.map(deserializeLayerPropertyValue), [
          "order",
        ]),
        order: property.order === null ? i : property.order,
      })),
      ["order"]
    ),
    pointInfoLinks: response.pointInfoLinks ? response.pointInfoLinks : [],
  };
}

function deserializeLayerPropertyValue(
  response: LayerPropertyValueResponse | string,
  i: number
): LayerPropertyValue {
  if (typeof response === "string") {
    return {
      id: i + 1,
      name: response,
      order: i,
      disableSettingsVariableIds: new Array<number>(0),
    };
  }
  return {
    ...response,
    order: response.order !== null ? response.order : i,
    disableSettingsVariableIds: response.disableSettingsVariableIds || [],
  };
}

function deserializeMapPosition(response: MapPositionResponse): MapPosition {
  return { lat: response.lat, lng: response.long, zoom: response.zoom };
}

function isAxiosResponse(
  error: unknown | AxiosResponse
): error is AxiosResponse {
  const typedError: AxiosResponse = error as AxiosResponse;
  return (
    typedError.hasOwnProperty("data") &&
    typedError.hasOwnProperty("status") &&
    typedError.hasOwnProperty("headers") &&
    typedError.hasOwnProperty("config")
  );
}

export const useSettings = (config: string) => {
  const fetch = async () => {
    try {
      const response = await axios.get<SettingsResponse>(
        `/api/${config}/settings`
      );

      return deserializeSettings(response.data);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 401) {
          throw new UnauthorizedError(401, error.response?.data.detail);
        }
      } else if (isAxiosResponse(error) && error.status === 401) {
        throw new UnauthorizedError(401, error.data.detail);
      }

      throw error;
    }
  };
  return useQuery(["settings", config], fetch, {
    cacheTime: Infinity,
    staleTime: Infinity,
    retry: 1,
  });
};

export const useSettingsContext = () => {
  return useOutletContext<{ settings: Settings }>();
};

export const useAbout = (config: string) => {
  const fetch = async () => {
    const response = await axios.get<About>(`/api/${config}/about`);
    return response.data;
  };
  return useQuery(["about", config], fetch, {
    cacheTime: Infinity,
    staleTime: Infinity,
  });
};

export const useUpdateAbout = (config: string) => {
  const queryClient = useQueryClient();
  const update = async (about: About) => {
    const response = await axios.put<About>(`/api/${config}/about`, about);
    return response.data;
  };
  return useMutation(update, {
    onSuccess: () => queryClient.invalidateQueries(["about", config]),
  });
};

export const useLoginRedirect = (config: string) => {
  const location = useLocation();

  return useMemo(
    () =>
      `/${config}/app/login?reffer=${encodeURIComponent(
        `${location.pathname}${location.search}`
      )}`,
    [location.pathname, location.search, config]
  );
};

export const useAboutValidation = () => {
  const { t } = useTranslation();
  return Yup.object().shape({
    projectName: Yup.string().required(
      t("validation.requiredField", { field: t("label.projectName") })
    ),
    client: Yup.string().required(
      t("validation.requiredField", { field: t("label.client") })
    ),
    contact: Yup.string().required(
      t("validation.requiredField", { field: t("label.contact") })
    ),
    description: Yup.string().required(
      t("validation.requiredField", { field: t("label.description") })
    ),
  });
};
